import { Injectable } from '@angular/core';
import { NativeDateAdapter } from '@angular/material/core';

/**
 * A DateAdapter that interprets and displays all dates in UTC,
 * preventing the browser's local timezone from shifting date values.
 */
@Injectable()
export class UtcDateAdapter extends NativeDateAdapter {

  override createDate(year: number, month: number, date: number): Date {
    return new Date(Date.UTC(year, month, date));
  }

  override parse(value: any): Date | null {
    if (typeof value === 'string' && value.length > 0) {
      // Try ISO parse first
      const timestamp = Date.parse(value);
      if (!isNaN(timestamp)) {
        return new Date(timestamp);
      }
    }
    return super.parse(value);
  }

  override getYear(date: Date): number {
    return date.getUTCFullYear();
  }

  override getMonth(date: Date): number {
    return date.getUTCMonth();
  }

  override getDate(date: Date): number {
    return date.getUTCDate();
  }

  override getDayOfWeek(date: Date): number {
    return date.getUTCDay();
  }

  override getNumDaysInMonth(date: Date): number {
    // Create a date for the 1st of the next month in UTC, then go back one day
    const lastDay = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0));
    return lastDay.getUTCDate();
  }

  override addCalendarDays(date: Date, days: number): Date {
    return new Date(Date.UTC(
      date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + days,
      date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds()
    ));
  }

  override addCalendarMonths(date: Date, months: number): Date {
    const targetMonth = date.getUTCMonth() + months;
    const result = new Date(Date.UTC(date.getUTCFullYear(), targetMonth, date.getUTCDate()));
    // Clamp the day if the target month has fewer days (e.g., Jan 31 + 1 month = Feb 28)
    if (result.getUTCMonth() !== ((targetMonth % 12) + 12) % 12) {
      result.setUTCDate(0); // go to last day of previous month
    }
    return result;
  }

  override addCalendarYears(date: Date, years: number): Date {
    return this.addCalendarMonths(date, years * 12);
  }

  override toIso8601(date: Date): string {
    return date.toISOString();
  }

  override today(): Date {
    const now = new Date();
    return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  }

  override clone(date: Date): Date {
    return new Date(date.getTime());
  }

  override format(date: Date, displayFormat: Object): string {
    const day = date.getUTCDate();
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();

    const monthNames = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];

    if (displayFormat === 'input') {
      return `${month + 1}/${day}/${year}`;
    }

    // Default display: "MMM DD, YYYY"
    return `${monthNames[month]} ${day}, ${year}`;
  }
}
